import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { CalendarDays, Check, ChevronsUpDown, MapPin, UsersRound } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUsers } from "@/features/admin/hooks/useUser";
import { cn } from "@/lib/utils";
import { useCounties } from "@/shared/hooks/useMetaData";

import { useCreateExpo, useExpo, useUpdateExpo } from "../hooks/useExpos";

const blank = {
  name: "",
  locationName: "",
  address: "",
  county: "",
  town: "",
  description: "",
  startDate: "",
  endDate: "",
  timezone: "Africa/Nairobi",
  managerUserId: "",
  creditSalesAllowed: false,
  creditApprovalThreshold: "",
  defaultCreditDays: "",
};

export function ExpoFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const { data: expo } = useExpo(id);
  const { data: users } = useUsers(1, 1000);
  const { data: counties = [], isLoading: countiesLoading } = useCounties();
  const create = useCreateExpo();
  const update = useUpdateExpo();
  const [form, setForm] = useState(blank);
  const [error, setError] = useState("");
  const [countyOpen, setCountyOpen] = useState(false);

  useEffect(() => {
    // Hydrate the controlled edit form when its asynchronous record arrives.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (expo) {
      setForm({
        name: expo.name,
        locationName: expo.locationName,
        address: expo.address ?? "",
        county: expo.county ?? "",
        town: expo.town ?? "",
        description: expo.description ?? "",
        startDate: expo.startDate.slice(0, 10),
        endDate: expo.endDate.slice(0, 10),
        timezone: expo.timezone,
        managerUserId: expo.managerUserId,
        creditSalesAllowed: expo.creditSalesAllowed,
        creditApprovalThreshold: expo.creditApprovalThreshold?.toString() ?? "",
        defaultCreditDays: expo.defaultCreditDays?.toString() ?? "",
      });
    }
  }, [expo]);

  const set = (key: keyof typeof form, value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (
      !form.name ||
      !form.locationName ||
      !form.startDate ||
      !form.endDate ||
      !form.managerUserId
    ) {
      setError("Name, location, dates, and manager are required.");
      return;
    }

    if (form.endDate < form.startDate) {
      setError("End date cannot be before start date.");
      return;
    }

    if (form.startDate && form.endDate) {
      const start = new Date(`${form.startDate}T00:00:00Z`);
      const end = new Date(`${form.endDate}T00:00:00Z`);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        setError("Select valid start and end dates.");
        return;
      }
    }

    const payload = {
      ...form,
      address: form.address || null,
      county: form.county || null,
      town: form.town || null,
      description: form.description || null,
      startDate: new Date(`${form.startDate}T00:00:00Z`).toISOString(),
      endDate: new Date(`${form.endDate}T23:59:59Z`).toISOString(),
      creditApprovalThreshold:
        form.creditSalesAllowed && form.creditApprovalThreshold
          ? Number(form.creditApprovalThreshold)
          : null,
      defaultCreditDays:
        form.creditSalesAllowed && form.defaultCreditDays
          ? Number(form.defaultCreditDays)
          : null,
    };

    try {
      const saved =
        editing && expo
          ? await update.mutateAsync({
              id: id!,
              data: { ...payload, rowVersion: expo.rowVersion },
            })
          : await create.mutateAsync(payload);

      navigate(`/expos/${saved.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save Expo.");
    }
  };

  const activeUsers = (users?.items ?? []).filter((u) => u.isActive);
  const saving = create.isPending || update.isPending;

  return (
    <form
      onSubmit={submit}
      className="mx-auto w-full max-w-6xl space-y-6 px-4 sm:px-6 lg:px-8"
    >
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">
          {editing ? "Edit Expo" : "Create Expo"}
        </h1>
        <p className="text-muted-foreground">
          Capture the event schedule, location, manager, and operating policy.
        </p>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="border-b bg-muted/30">
          <CardTitle className="flex items-center gap-2 text-xl">
            <CalendarDays className="h-5 w-5 text-primary" />
            Expo event details
          </CardTitle>
          <CardDescription>
            Define the event calendar, venue, ownership, and credit policy.
          </CardDescription>
        </CardHeader>

        <CardContent className="grid gap-8 pt-6">
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <MapPin className="h-4 w-4 text-primary" />
              Event and venue
            </div>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              <Field>
                <FieldLabel>Name *</FieldLabel>
                <Input
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Nairobi Farmers Market"
                />
              </Field>

              <Field>
                <FieldLabel>Location *</FieldLabel>
                <Input
                  value={form.locationName}
                  onChange={(e) => set("locationName", e.target.value)}
                  placeholder="e.g. KICC Grounds"
                />
              </Field>

              <Field>
                <FieldLabel>Timezone</FieldLabel>
                <Input
                  value={form.timezone}
                  onChange={(e) => set("timezone", e.target.value)}
                  placeholder="Africa/Nairobi"
                />
              </Field>

              <Field>
                <FieldLabel>Start date *</FieldLabel>
                <DatePicker
                  value={form.startDate}
                  onChange={(value) => set("startDate", value)}
                  max={form.endDate || undefined}
                  placeholder="Select start date"
                />
              </Field>

              <Field>
                <FieldLabel>End date *</FieldLabel>
                <DatePicker
                  value={form.endDate}
                  onChange={(value) => set("endDate", value)}
                  min={form.startDate || undefined}
                  placeholder="Select end date"
                />
              </Field>

              <Field>
                <FieldLabel>County</FieldLabel>
                <Popover open={countyOpen} onOpenChange={setCountyOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={countyOpen}
                      className={cn(
                        "w-full justify-between font-normal",
                        !form.county && "text-muted-foreground",
                      )}
                      disabled={countiesLoading}
                    >
                      {form.county || (countiesLoading ? "Loading counties…" : "Search county")}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search county..." />
                      <CommandList>
                        <CommandEmpty>No county found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="clear-county"
                            onSelect={() => {
                              set("county", "");
                              setCountyOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", !form.county ? "opacity-100" : "opacity-0")} />
                            No county selected
                          </CommandItem>
                          {counties.map((county) => (
                            <CommandItem
                              key={county.code}
                              value={`${county.name} ${county.code} ${county.countyNumber}`}
                              onSelect={() => {
                                set("county", county.name);
                                setCountyOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", form.county === county.name ? "opacity-100" : "opacity-0")} />
                              {county.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </Field>

              <Field>
                <FieldLabel>Town</FieldLabel>
                <Input
                  value={form.town}
                  onChange={(e) => set("town", e.target.value)}
                  placeholder="Town"
                />
              </Field>

              <Field className="md:col-span-2">
                <FieldLabel>Address</FieldLabel>
                <Input
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder="Street, building, landmark, booth area"
                />
              </Field>

              <Field className="md:col-span-2 xl:col-span-3">
                <FieldLabel>Description</FieldLabel>
                <Textarea
                  rows={5}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Notes for operations, logistics, or event context"
                />
              </Field>
            </div>
          </section>

          <section className="space-y-4 rounded-lg border bg-muted/20 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <UsersRound className="h-4 w-4 text-primary" />
              Manager and credit policy
            </div>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              <Field className="xl:col-span-2">
                <FieldLabel>Manager *</FieldLabel>
                <Select
                  value={form.managerUserId || undefined}
                  onValueChange={(value) => set("managerUserId", value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeUsers.map((user) => (
                      <SelectItem value={user.id} key={user.id}>
                        {[user.firstName, user.otherNames]
                          .filter(Boolean)
                          .join(" ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <label className="flex min-h-20 items-center gap-3 rounded-lg border bg-background p-4 text-sm">
                <Checkbox
                  checked={form.creditSalesAllowed}
                  onCheckedChange={(checked) =>
                    set("creditSalesAllowed", checked === true)
                  }
                />
                <span>
                  <span className="block font-medium">Allow credit sales</span>
                  <span className="text-muted-foreground">
                    Enable paid-later sales for this Expo.
                  </span>
                </span>
              </label>

              {form.creditSalesAllowed && (
                <>
                  <Field>
                    <FieldLabel>Finance approval threshold</FieldLabel>
                    <Input
                      type="number"
                      min="0"
                      value={form.creditApprovalThreshold}
                      onChange={(e) =>
                        set("creditApprovalThreshold", e.target.value)
                      }
                      placeholder="0.00"
                    />
                  </Field>

                  <Field>
                    <FieldLabel>Default credit days</FieldLabel>
                    <Input
                      type="number"
                      min="0"
                      value={form.defaultCreditDays}
                      onChange={(e) => set("defaultCreditDays", e.target.value)}
                      placeholder="e.g. 7"
                    />
                  </Field>
                </>
              )}
            </div>
          </section>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
        </CardContent>

        <CardFooter className="justify-end gap-3 border-t bg-muted/20">
          <Button type="button" variant="outline" asChild>
            <Link to={editing ? `/expos/${id}` : "/expos"}>Cancel</Link>
          </Button>
          <Button disabled={saving}>
            {saving ? "Saving…" : "Save Expo"}
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
}
